import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, Chip, Dialog, DialogActions,
  DialogContent, DialogTitle, FormControl, Grid, InputLabel, MenuItem,
  Select, Tab, Tabs, TextField, Typography,
} from '@mui/material';
import { Add, Refresh } from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import { useTheme } from '@mui/material/styles';

import CustomPagination from '../../components/common/CustomPagination';
import {
  approveMarketplaceDisposition,
  approveMarketplaceExitRequest,
  completeMarketplaceLegalReview,
  createMarketplaceStorageAgreement,
  executeMarketplaceDisposition,
  getMarketplaceDispositionCases,
  getMarketplaceExitRequests,
  getMarketplaceStorageAccounts,
  getMarketplaceStorageAgreements,
  recordMarketplaceDispositionNotice,
  refreshMarketplaceOverdueAccounts,
  restoreMarketplaceStorageAccount,
} from '../../services/marketplaceWarehouse';

const initialAgreement = { storage_account_sid: '', version: '', terms_url: '', terms_sha256: '', terms_snapshot: '' };
const initialReview = { mode: '', sid: '', notes: '', legal_basis: '', notice_type: 'initial', channel: 'dashboard', recipient: '', subject: '', body: '' };

const WarehouseLifecycleAdminPage = () => {
  const theme = useTheme();
  const [tab, setTab] = useState(0);
  const [accounts, setAccounts] = useState([]);
  const [agreements, setAgreements] = useState([]);
  const [exitRequests, setExitRequests] = useState([]);
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [agreementOpen, setAgreementOpen] = useState(false);
  const [agreementForm, setAgreementForm] = useState(initialAgreement);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewForm, setReviewForm] = useState(initialReview);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [customRowsInput, setCustomRowsInput] = useState('');
  const [customRowsActive, setCustomRowsActive] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [accountResult, agreementResult, exitResult, caseResult] = await Promise.all([
        getMarketplaceStorageAccounts(), getMarketplaceStorageAgreements(),
        getMarketplaceExitRequests(), getMarketplaceDispositionCases(),
      ]);
      setAccounts(accountResult.rows);
      setAgreements(agreementResult.rows);
      setExitRequests(exitResult.rows);
      setCases(caseResult.rows);
    } catch (requestError) {
      setError(JSON.stringify(requestError.response?.data || requestError.message));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const perform = async (callback) => {
    setSaving(true);
    setError('');
    try {
      await callback();
      setReviewOpen(false);
      setReviewForm(initialReview);
      await loadData();
    } catch (requestError) {
      setError(JSON.stringify(requestError.response?.data || requestError.message));
    } finally {
      setSaving(false);
    }
  };

  const openReview = (mode, sid) => {
    setReviewForm({ ...initialReview, mode, sid });
    setReviewOpen(true);
  };

  const submitReview = () => perform(() => {
    if (reviewForm.mode === 'exit') return approveMarketplaceExitRequest(reviewForm.sid, { review_notes: reviewForm.notes });
    if (reviewForm.mode === 'legal') return completeMarketplaceLegalReview(reviewForm.sid, { legal_basis: reviewForm.legal_basis, notes: reviewForm.notes });
    return recordMarketplaceDispositionNotice(reviewForm.sid, {
      notice_type: reviewForm.notice_type, channel: reviewForm.channel,
      recipient: reviewForm.recipient, subject: reviewForm.subject, body: reviewForm.body,
    });
  });

  const createAgreement = () => perform(async () => {
    await createMarketplaceStorageAgreement(agreementForm);
    setAgreementOpen(false);
    setAgreementForm(initialAgreement);
  });

  const accountColumns = [
    { field: 'merchant_name', headerName: 'Merchant', flex: 1, minWidth: 190 },
    { field: 'warehouse_name', headerName: 'Warehouse', flex: 1, minWidth: 170 },
    { field: 'status', headerName: 'Status', width: 120, renderCell: ({ value }) => <Chip size="small" label={value} color={value === 'active' ? 'success' : 'warning'} /> },
    { field: 'overdue_started_at', headerName: 'Overdue Since', width: 190 },
    { field: 'restricted_at', headerName: 'Restricted At', width: 190 },
    { field: 'final_notice_at', headerName: 'Final Notice At', width: 190 },
    { field: 'legal_review_at', headerName: 'Legal Review At', width: 190 },
    { field: 'actions', headerName: 'Actions', width: 150, sortable: false, renderCell: ({ row }) => <Button size="small" disabled={saving} onClick={() => perform(() => restoreMarketplaceStorageAccount(row.sid))}>Restore</Button> },
  ];
  const agreementColumns = [
    { field: 'storage_account_sid', headerName: 'Storage Account SID', flex: 1, minWidth: 250 },
    { field: 'version', headerName: 'Version', width: 140 },
    { field: 'status', headerName: 'Status', width: 130, renderCell: ({ value }) => <Chip size="small" label={value} /> },
    { field: 'terms_url', headerName: 'Terms URL', flex: 1, minWidth: 220 },
    { field: 'accepted_at', headerName: 'Accepted At', width: 190 },
  ];
  const exitColumns = [
    { field: 'merchant_name', headerName: 'Merchant', flex: 1, minWidth: 190 },
    { field: 'warehouse_name', headerName: 'Warehouse', flex: 1, minWidth: 170 },
    { field: 'request_type', headerName: 'Request', width: 160 },
    { field: 'status', headerName: 'Status', width: 130, renderCell: ({ value }) => <Chip size="small" label={value} /> },
    { field: 'created_at', headerName: 'Requested At', width: 190 },
    { field: 'actions', headerName: 'Actions', width: 130, sortable: false, renderCell: ({ row }) => row.status === 'pending' && <Button size="small" onClick={() => openReview('exit', row.sid)}>Review</Button> },
  ];
  const caseColumns = [
    { field: 'case_number', headerName: 'Case Number', width: 180 },
    { field: 'merchant_name', headerName: 'Merchant', flex: 1, minWidth: 180 },
    { field: 'disposition_type', headerName: 'Disposition', width: 140 },
    { field: 'source', headerName: 'Source', width: 140 },
    { field: 'status', headerName: 'Status', width: 150, renderCell: ({ value }) => <Chip size="small" label={value} /> },
    {
      field: 'actions', headerName: 'Actions', width: 430, sortable: false,
      renderCell: ({ row }) => <Box display="flex" gap={0.5}>
        {row.status === 'legal_review' && <Button size="small" onClick={() => openReview('legal', row.sid)}>Legal Review</Button>}
        {['pending_approval', 'approved'].includes(row.status) && <Button size="small" onClick={() => perform(() => approveMarketplaceDisposition(row.sid))}>Approve</Button>}
        {row.status === 'approved' && <Button size="small" color="error" onClick={() => perform(() => executeMarketplaceDisposition(row.sid))}>Execute</Button>}
        {row.status !== 'executed' && <Button size="small" onClick={() => openReview('notice', row.sid)}>Record Notice</Button>}
      </Box>,
    },
  ];

  const table = [
    [accounts, accountColumns], [agreements, agreementColumns],
    [exitRequests, exitColumns], [cases, caseColumns],
  ][tab];
  const gridRows = table[0].map((row) => ({ ...row, id: row.sid }));
  const totalPages = Math.ceil(gridRows.length / rowsPerPage);
  const paginatedRows = gridRows.slice(page * rowsPerPage, (page + 1) * rowsPerPage);

  useEffect(() => { setPage(0); }, [tab]);
  useEffect(() => {
    if (totalPages > 0 && page >= totalPages) setPage(totalPages - 1);
  }, [page, totalPages]);

  const handlePageChange = (newPage) => {
    if (newPage < 0 || newPage >= totalPages) return;
    setPage(newPage);
  };
  const handleRowsPerPageChange = (event) => {
    const value = event.target.value;
    if (value === 'custom') {
      setCustomRowsActive(true);
      setCustomRowsInput('');
      return;
    }
    setRowsPerPage(Number(value));
    setCustomRowsActive(false);
    setCustomRowsInput('');
    setPage(0);
  };
  const handleCustomRowsInputChange = (event) => setCustomRowsInput(event.target.value.replace(/\D/g, ''));
  const handleCustomRowsInputBlur = () => {
    const numericValue = Number(customRowsInput);
    if (numericValue > 0) {
      setRowsPerPage(numericValue);
      setPage(0);
    }
    setCustomRowsActive(false);
    setCustomRowsInput('');
  };

  return (
    <Box sx={{
      minHeight: '100vh',
      backgroundColor: 'background.default',
      px: { xs: 0.75, sm: 1, md: 1.5 },
      py: { xs: 2, sm: 2.5, md: 3 },
      maxWidth: '95vw',
      overflowX: 'hidden',
    }}>
      <Box display="flex" justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} flexWrap="wrap" gap={2} mb={2}>
        <Box>
          <Typography variant="h4">Warehouse Lifecycle Operations</Typography>
          <Typography color="text.secondary">Manage storage terms, overdue restrictions, seller exits, notices, legal reviews, and disposition approvals.</Typography>
        </Box>
        <Box display="flex" gap={1} flexWrap="wrap">
          <Button startIcon={<Refresh />} onClick={loadData}>Refresh</Button>
          <Button onClick={() => perform(refreshMarketplaceOverdueAccounts)}>Evaluate Overdue</Button>
          <Button variant="contained" startIcon={<Add />} onClick={() => setAgreementOpen(true)}>Publish Terms</Button>
        </Box>
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Alert severity="warning" sx={{ mb: 2 }}>Liquidation and destruction require legal review and approvals from two different staff users. Execution is a separate action.</Alert>
      <Card><CardContent>
        <Tabs value={tab} onChange={(_, value) => setTab(value)} sx={{ mb: 2 }}>
          <Tab label={`Storage Accounts (${accounts.length})`} />
          <Tab label={`Storage Terms (${agreements.length})`} />
          <Tab label={`Exit Requests (${exitRequests.length})`} />
          <Tab label={`Disposition Cases (${cases.length})`} />
        </Tabs>
        <DataGrid
          autoHeight
          loading={loading}
          rows={paginatedRows}
          columns={table[1]}
          disableRowSelectionOnClick
          hideFooter
          sx={{
            border: 'none',
            '& .MuiDataGrid-columnHeader': { backgroundColor: theme.palette.primary.main },
            '& .MuiDataGrid-columnHeaderTitle': { color: theme.palette.primary.contrastText, fontWeight: 'bold' },
            '& .MuiDataGrid-sortIcon, & .MuiDataGrid-menuIconButton': { color: theme.palette.primary.contrastText },
            '& .MuiDataGrid-row:hover': { backgroundColor: theme.palette.action.hover },
          }}
        />
      </CardContent></Card>
      <CustomPagination
        page={page}
        totalPages={totalPages}
        rowsPerPage={rowsPerPage}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
        customRowsActive={customRowsActive}
        customRowsInput={customRowsInput}
        onCustomRowsInputChange={handleCustomRowsInputChange}
        onCustomRowsInputBlur={handleCustomRowsInputBlur}
      />

      <Dialog open={agreementOpen} onClose={() => setAgreementOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Publish Storage Terms</DialogTitle>
        <DialogContent><Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid size={{ xs: 12 }}><FormControl fullWidth required><InputLabel>Storage Account</InputLabel><Select label="Storage Account" value={agreementForm.storage_account_sid} onChange={(event) => setAgreementForm({ ...agreementForm, storage_account_sid: event.target.value })}>{accounts.map((account) => <MenuItem value={account.sid} key={account.sid}>{account.merchant_name} - {account.warehouse_name}</MenuItem>)}</Select></FormControl></Grid>
          <Grid size={{ xs: 12, md: 4 }}><TextField fullWidth required label="Version" value={agreementForm.version} onChange={(event) => setAgreementForm({ ...agreementForm, version: event.target.value })} /></Grid>
          <Grid size={{ xs: 12, md: 8 }}><TextField fullWidth label="Terms URL" value={agreementForm.terms_url} onChange={(event) => setAgreementForm({ ...agreementForm, terms_url: event.target.value })} /></Grid>
          <Grid size={{ xs: 12 }}><TextField fullWidth required label="SHA-256 Hash" value={agreementForm.terms_sha256} onChange={(event) => setAgreementForm({ ...agreementForm, terms_sha256: event.target.value })} /></Grid>
          <Grid size={{ xs: 12 }}><TextField fullWidth required multiline minRows={6} label="Terms Snapshot" value={agreementForm.terms_snapshot} onChange={(event) => setAgreementForm({ ...agreementForm, terms_snapshot: event.target.value })} /></Grid>
        </Grid></DialogContent>
        <DialogActions><Button onClick={() => setAgreementOpen(false)}>Cancel</Button><Button variant="contained" disabled={saving || !agreementForm.storage_account_sid || !agreementForm.version || agreementForm.terms_sha256.length !== 64 || !agreementForm.terms_snapshot} onClick={createAgreement}>Publish</Button></DialogActions>
      </Dialog>

      <Dialog open={reviewOpen} onClose={() => setReviewOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{reviewForm.mode === 'exit' ? 'Approve Exit Request' : reviewForm.mode === 'legal' ? 'Complete Legal Review' : 'Record Seller Notice'}</DialogTitle>
        <DialogContent><Grid container spacing={2} sx={{ mt: 0.5 }}>
          {reviewForm.mode === 'legal' && <Grid size={{ xs: 12 }}><TextField fullWidth required multiline minRows={3} label="Legal Basis" value={reviewForm.legal_basis} onChange={(event) => setReviewForm({ ...reviewForm, legal_basis: event.target.value })} /></Grid>}
          {reviewForm.mode === 'notice' && <>
            <Grid size={{ xs: 6 }}><FormControl fullWidth><InputLabel>Notice Type</InputLabel><Select label="Notice Type" value={reviewForm.notice_type} onChange={(event) => setReviewForm({ ...reviewForm, notice_type: event.target.value })}><MenuItem value="initial">Initial</MenuItem><MenuItem value="reminder">Reminder</MenuItem><MenuItem value="final">Final</MenuItem><MenuItem value="legal">Legal</MenuItem></Select></FormControl></Grid>
            <Grid size={{ xs: 6 }}><FormControl fullWidth><InputLabel>Channel</InputLabel><Select label="Channel" value={reviewForm.channel} onChange={(event) => setReviewForm({ ...reviewForm, channel: event.target.value })}><MenuItem value="dashboard">Dashboard</MenuItem><MenuItem value="email">Email</MenuItem><MenuItem value="sms">SMS</MenuItem><MenuItem value="letter">Letter</MenuItem></Select></FormControl></Grid>
            <Grid size={{ xs: 12 }}><TextField fullWidth required label="Recipient" value={reviewForm.recipient} onChange={(event) => setReviewForm({ ...reviewForm, recipient: event.target.value })} /></Grid>
            <Grid size={{ xs: 12 }}><TextField fullWidth required label="Subject" value={reviewForm.subject} onChange={(event) => setReviewForm({ ...reviewForm, subject: event.target.value })} /></Grid>
            <Grid size={{ xs: 12 }}><TextField fullWidth required multiline minRows={4} label="Notice Body" value={reviewForm.body} onChange={(event) => setReviewForm({ ...reviewForm, body: event.target.value })} /></Grid>
          </>}
          {reviewForm.mode !== 'notice' && <Grid size={{ xs: 12 }}><TextField fullWidth multiline minRows={3} label="Notes" value={reviewForm.notes} onChange={(event) => setReviewForm({ ...reviewForm, notes: event.target.value })} /></Grid>}
        </Grid></DialogContent>
        <DialogActions><Button onClick={() => setReviewOpen(false)}>Cancel</Button><Button variant="contained" disabled={saving || (reviewForm.mode === 'legal' && !reviewForm.legal_basis) || (reviewForm.mode === 'notice' && (!reviewForm.recipient || !reviewForm.subject || !reviewForm.body))} onClick={submitReview}>Submit</Button></DialogActions>
      </Dialog>
    </Box>
  );
};

export default WarehouseLifecycleAdminPage;
